import React from 'react';
import styled from 'styled-components';
import { FaDiscord } from 'react-icons/fa';
import { FiArrowRight, FiClock, FiMonitor, FiMessageCircle } from 'react-icons/fi';

const aiModels = [
  { provider: 'Google', name: 'Gemini', mark: 'G', color: '#8cbaff' },
  { provider: 'Anthropic', name: 'Claude', mark: 'C', color: '#edbca1' },
  { provider: 'OpenAI', name: 'ChatGPT', mark: 'O', color: '#9bd8c3' },
];

const courseWeeks = [
  { week: '01', title: '문제를 정하고 기획합니다', copy: '관심사에서 질문을 찾고, 만들 서비스의 화면과 기능을 설계합니다.' },
  { week: '02', title: 'AI와 기능을 구현합니다', copy: '프로젝트에 알맞은 AI와 대화하며 코드를 만들고 직접 실행합니다.' },
  { week: '03', title: '완성도를 높여 배포합니다', copy: '오류를 고치고 피드백을 반영해 실제로 접속할 수 있는 링크를 만듭니다.' },
  { week: '04', title: '공개하고 다음으로 갑니다', copy: '완성한 결과물을 발표하고 받은 반응을 다음 프로젝트에 연결합니다.' },
];

const courseBenefits = [
  { value: '10시간', label: '월 코칭' },
  { value: '1 : 4', label: '최대 그룹' },
  { value: '자유 예약', label: '내 일정에 맞춰' },
  { value: '24H · 7D', label: 'AI 작업실 이용' },
];

const Section = styled.section`
  position: relative;
  padding: clamp(88px, 9vw, 132px) 0;
  scroll-margin-top: 80px;
  background: #10151e;
  color: #f4f7fb;
  overflow: hidden;

  * { box-sizing: border-box; }

  h2, h3, h4, p { word-break: keep-all; overflow-wrap: break-word; }

  .section-label {
    display: flex;
    align-items: center;
    gap: 14px;
    color: #9fb4cf;
    font-size: 0.75rem;
    font-weight: 650;
    letter-spacing: 0.1em;
  }

  .section-label::before {
    content: '';
    width: 28px;
    height: 1px;
    background: #83acdf;
  }
`;

const CourseShell = styled.div`
  width: min(calc(100% - 72px), 1180px);
  margin-inline: auto;

  @media (max-width: 768px) {
    width: calc(100% - 40px);
  }
`;

const EditorialHeader = styled.header`
  max-width: 940px;
  margin-bottom: clamp(56px, 7vw, 92px);

  h2 {
    margin: 25px 0 0;
    color: #f4f7fb;
    font-size: clamp(2.25rem, 4.25vw, 4rem);
    font-weight: 760;
    letter-spacing: -0.052em;
    line-height: 1.2;
  }

  h2 span {
    display: block;
    margin-top: 22px;
    color: #a5b9d3;
    font-size: clamp(1.1rem, 2vw, 1.65rem);
    font-weight: 480;
    letter-spacing: -0.025em;
    line-height: 1.6;
  }

  > p {
    max-width: 640px;
    margin: 25px 0 0;
    color: #abb7c7;
    font-size: clamp(1rem, 1.4vw, 1.08rem);
    line-height: 1.85;
    font-weight: 400;
  }

  .course-meta {
    display: flex;
    flex-wrap: wrap;
    gap: 9px 22px;
    margin-top: 26px;
    color: #d5dce7;
    font-size: 0.83rem;
    line-height: 1.6;
  }

  .course-meta span + span::before {
    content: '·';
    margin-right: 22px;
    color: #637188;
  }

  @media (max-width: 540px) {
    h2 { font-size: 2.3rem; }
    h2 span { margin-top: 18px; font-size: 1.1rem; }
    .course-meta { gap: 8px 14px; font-size: 0.76rem; }
    .course-meta span + span::before { margin-right: 14px; }
  }
`;

const StoryRow = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 0.95fr) minmax(0, 1fr);
  align-items: center;
  gap: clamp(44px, 7vw, 100px);
  padding: clamp(48px, 6vw, 78px) 0;
  border-top: 1px solid rgba(189, 207, 232, 0.14);

  .story-copy { min-width: 0; }

  h3 {
    margin: 0 0 24px;
    color: #f4f7fb;
    font-size: clamp(1.8rem, 2.6vw, 2.65rem);
    font-weight: 680;
    line-height: 1.35;
    letter-spacing: -0.04em;
  }

  h3 span { display: block; color: #94b9eb; }

  .story-copy > p:first-of-type {
    margin: 0;
    color: #aebacb;
    font-size: 1.02rem;
    font-weight: 400;
    line-height: 1.9;
  }

  .plain-language {
    margin-top: 26px;
    padding-left: 18px;
    border-left: 2px solid #6b94ca;
    color: #d0d9e6;
    font-size: 0.9rem;
    line-height: 1.85;
  }

  @media (max-width: 820px) {
    grid-template-columns: minmax(0, 1fr);
    gap: 32px;
    align-items: start;
  }

  @media (max-width: 540px) {
    h3 { font-size: 1.85rem; margin-bottom: 20px; }
    .story-copy > p:first-of-type { font-size: 0.98rem; }
    .plain-language { font-size: 0.86rem; }
  }
`;

const Workspace = styled.div`
  min-width: 0;
  padding: clamp(25px, 3.2vw, 42px);
  border: 1px solid rgba(156, 190, 232, 0.19);
  border-radius: 24px;
  background: linear-gradient(145deg, #1b2a3e, #152031);

  .workspace-title {
    display: flex;
    align-items: center;
    gap: 10px;
    color: #c5d9f2;
    font-size: 0.86rem;
    font-weight: 600;
  }

  .workspace-title::before {
    content: '';
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: #95c6b4;
  }

  .project-flow {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 14px;
    margin: 36px 0 0;
    padding-top: 28px;
    border-top: 1px solid rgba(190, 211, 238, 0.15);
    color: #e5edf8;
    font-size: clamp(1.1rem, 1.8vw, 1.5rem);
    font-weight: 600;
    letter-spacing: -0.03em;
  }

  .project-flow svg { color: #7c9bc2; font-size: 1rem; }

  .workspace-hours {
    display: grid;
    grid-template-columns: 20px minmax(0, 1fr);
    column-gap: 10px;
    row-gap: 6px;
    margin-top: 30px;
    color: #d1ddec;
    font-size: 0.86rem;
    line-height: 1.6;
  }

  .workspace-hours svg { margin-top: 3px; color: #91b3e1; }
  .workspace-hours strong { font-weight: 600; }
  .workspace-hours span { grid-column: 2; color: #99acc4; font-size: 0.82rem; }
`;

const ModelList = styled.ul`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 14px;
  padding: 0;
  margin: 34px 0 0;
  list-style: none;

  li { min-width: 0; }

  .model-mark {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 42px;
    height: 42px;
    margin-bottom: 16px;
    border: 1px solid currentColor;
    border-radius: 14px;
    color: var(--model-color);
    font-size: 1.25rem;
    font-weight: 650;
  }

  small, strong { display: block; }
  small { margin-bottom: 5px; color: #9aacc2; font-size: 0.72rem; }
  strong { color: #edf3fc; font-size: 1.08rem; font-weight: 600; letter-spacing: -0.03em; }

  @media (max-width: 540px) {
    gap: 10px;
    strong { font-size: 0.98rem; }
    small { font-size: 0.66rem; }
  }
`;

const ClassFlow = styled.ol`
  display: grid;
  gap: 28px;
  margin: 0;
  padding: 0;
  list-style: none;

  li {
    display: grid;
    grid-template-columns: 46px minmax(0, 1fr);
    align-items: start;
    column-gap: 18px;
    row-gap: 7px;
  }

  .class-icon {
    grid-row: 1 / span 2;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 46px;
    height: 46px;
    border: 1px solid rgba(163, 187, 225, 0.16);
    border-radius: 14px;
    color: #a4bff6;
    font-size: 1.25rem;
  }

  strong { color: #e9eef6; font-size: 1.05rem; font-weight: 580; }
  li > span:last-child { color: #9faec3; font-size: 0.9rem; line-height: 1.7; }
`;

const AvailabilityNote = styled.p`
  margin: 26px 0 0;
  padding-top: 24px;
  border-top: 1px solid rgba(189, 207, 232, 0.14);
  color: #95a6bd;
  font-size: 0.85rem;
  line-height: 1.85;
  word-break: keep-all;
`;

const CourseJourney = styled.div`
  padding-top: clamp(48px, 6vw, 78px);
  border-top: 1px solid rgba(189, 207, 232, 0.14);

  > header {
    display: flex;
    flex-wrap: wrap;
    align-items: end;
    justify-content: space-between;
    gap: 16px 36px;
  }

  h3 {
    max-width: 650px;
    margin: 0;
    color: #f4f7fb;
    font-size: clamp(1.7rem, 2.4vw, 2.25rem);
    font-weight: 650;
    line-height: 1.4;
    letter-spacing: -0.035em;
  }

  header p { margin: 0; color: #9db0ca; font-size: 0.9rem; line-height: 1.7; }
`;

const WeekList = styled.ol`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 30px;
  padding: 0;
  margin: 48px 0 0;
  list-style: none;

  li { min-width: 0; padding-top: 22px; border-top: 1px solid #4b6585; }
  .week { color: #86a8d5; font-size: 0.77rem; letter-spacing: 0.05em; }
  h4 { margin: 20px 0 12px; color: #e8eef7; font-size: 1.12rem; font-weight: 600; line-height: 1.6; letter-spacing: -0.025em; }
  p { margin: 0; color: #9faec3; font-size: 0.92rem; line-height: 1.85; }

  @media (max-width: 900px) { grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 36px 30px; }
  @media (max-width: 540px) {
    grid-template-columns: minmax(0, 1fr);
    gap: 28px;
    margin-top: 30px;
    li { display: grid; grid-template-columns: 52px minmax(0, 1fr); gap: 6px 18px; padding-top: 24px; }
    .week { grid-row: 1 / span 2; margin-top: 6px; }
    h4 { margin: 0; font-size: 1.05rem; }
    p { grid-column: 2; font-size: 0.88rem; }
  }
`;

const SupportStrip = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 24px;
  margin-top: 64px;
  padding-top: 34px;
  border-top: 1px solid rgba(189, 207, 232, 0.14);

  .benefit { display: flex; flex-direction: column; gap: 9px; }
  strong { color: #dfe8f5; font-size: clamp(1.3rem, 2vw, 1.65rem); font-weight: 550; letter-spacing: -0.025em; }
  span { color: #9caec5; font-size: 0.85rem; line-height: 1.6; }
  > small { grid-column: 1 / -1; margin-top: 6px; color: #8193ad; font-size: 0.77rem; line-height: 1.85; word-break: keep-all; }

  @media (max-width: 540px) { grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 28px; margin-top: 42px; }
`;

const RegularCourseIntro = () => (
  <Section id="regular-course-intro" aria-labelledby="regular-course-intro-title">
    <CourseShell>
      <EditorialHeader>
        <div className="section-label">01 / 정기과정</div>
        <h2 id="regular-course-intro-title">
          AI마다 잘하는 일은 다릅니다.
          <span>알맞은 준비는 나니아가, 만드는 경험은 아이가.</span>
        </h2>
        <p>최신 AI가 바뀔 때마다 설정을 다시 배울 필요는 없습니다. 나니아가 모델과 도구를 한곳에서 관리해, 아이는 한 번의 로그인으로 프로젝트를 이어갑니다.</p>
        <div className="course-meta" aria-label="정기과정 핵심 정보">
          <span>초4–고3</span><span>온라인</span><span>한 달에 프로젝트 하나</span>
        </div>
      </EditorialHeader>

      <StoryRow>
        <div className="story-copy">
          <h3>아이에게 필요한 AI를,<span>하나의 작업실에.</span></h3>
          <p>질문과 작업이 달라지면 잘 맞는 AI도 달라집니다. Gemini·Claude·ChatGPT를 하나의 작업실에서 선택해 프로젝트를 이어갑니다.</p>
          <div className="plain-language">검색·파일·서버 같은 기능 연결과 업데이트는 나니아가 준비합니다. 아이는 복잡한 설치 대신 질문하고, 만들고, 실행하는 데 집중합니다.</div>
        </div>
        <Workspace>
          <div className="workspace-title">나니아 AI 작업실</div>
          <ModelList aria-label="정기과정에서 사용하는 AI 모델">
            {aiModels.map(model => (
              <li key={model.name} style={{ '--model-color': model.color }}>
                <span className="model-mark" aria-hidden="true">{model.mark}</span>
                <small>{model.provider}</small><strong>{model.name}</strong>
              </li>
            ))}
          </ModelList>
          <div className="project-flow" aria-label="프로젝트 진행 흐름">
            <span>질문</span><FiArrowRight aria-hidden="true" /><span>제작</span><FiArrowRight aria-hidden="true" /><span>공개</span>
          </div>
          <div className="workspace-hours">
            <FiClock aria-hidden="true" /><strong>24H · 7D AI 작업실</strong>
            <span>수업 밖에서도 프로젝트를 자유롭게 이어갑니다.</span>
          </div>
        </Workspace>
      </StoryRow>

      <StoryRow as="aside" aria-labelledby="online-class-title">
        <div className="story-copy">
          <h3 id="online-class-title">온라인에서 만나고,<br />Discord에서 함께 만듭니다.</h3>
          <p>정기과정은 온라인으로 진행하며, 주로 Discord 음성 채널에서 라이브 미팅과 화면 공유를 합니다. 멘토와 프로젝트를 함께 보고 질문하고 피드백을 반영합니다.</p>
          <AvailabilityNote>사람 멘토링은 예약한 수업 시간에 진행되며, 개인 AI 작업실은 수업 밖에서도 하루 24시간 접속할 수 있습니다.</AvailabilityNote>
        </div>
        <ClassFlow aria-label="Discord 온라인 정기과정 진행 방식">
          <li><span className="class-icon" aria-hidden="true"><FaDiscord /></span><strong>Discord 라이브 미팅</strong><span>정해진 시간에 음성 채널에서 만납니다.</span></li>
          <li><span className="class-icon" aria-hidden="true"><FiMonitor /></span><strong>화면 공유·제작</strong><span>같은 화면을 보며 코드와 결과물을 만듭니다.</span></li>
          <li><span className="class-icon" aria-hidden="true"><FiMessageCircle /></span><strong>질문·피드백</strong><span>막힌 부분을 묻고 다음 작업 방향을 정합니다.</span></li>
        </ClassFlow>
      </StoryRow>

      <CourseJourney>
        <header><h3>정기과정을 시작하면,<br />첫 달은 이렇게 흘러갑니다.</h3><p>기획부터 공개까지 4주 프로젝트</p></header>
        <WeekList aria-label="정기과정 등록 후 4주 진행 과정">
          {courseWeeks.map(item => (
            <li key={item.week}><span className="week">{item.week}주 차</span><h4>{item.title}</h4><p>{item.copy}</p></li>
          ))}
        </WeekList>
        <SupportStrip aria-label="정기과정 운영 혜택">
          {courseBenefits.map(benefit => <div className="benefit" key={benefit.value}><strong>{benefit.value}</strong><span>{benefit.label}</span></div>)}
          <small>세부 운영 시간과 그룹 구성, 제공 모델은 과정 및 기수와 서비스 업데이트에 따라 달라질 수 있습니다.</small>
        </SupportStrip>
      </CourseJourney>
    </CourseShell>
  </Section>
);

export default RegularCourseIntro;
